import React, { useEffect, useRef, useState } from "react"
import { loadStripe } from "@stripe/stripe-js"

import CatchModal from "~/library/CatchModal"
import getAppConfig from "~/utils/getAppConfig"
import { apiSavedPaymentMethods } from "~/routes"
import { useToast } from "~/utils/Toast"
import type SavedPaymentMethod from "~/types/SavedPaymentMethod"

type PayableType = "booking" | "deal" | "sponsorship"
type SavePaymentMethodMode = "add" | "update"

interface PaymentOwnerDetails {
  name?: string
  email?: string
  phone?: string
}

interface Props {
  payableType: PayableType
  payableId: string
  stripePublishableKey: string
  paymentOwner?: PaymentOwnerDetails | null
  mode?: SavePaymentMethodMode
  existingSavedPaymentMethod?: SavedPaymentMethod | null
  onClose: () => void
  onSuccess: () => Promise<void> | void
}

const isDarkModeEnabled = () => {
  return (
    document.documentElement.classList.contains("dark") ||
    document.body.classList.contains("dark") ||
    document.documentElement.getAttribute("data-bs-theme") === "dark" ||
    document.body.getAttribute("data-bs-theme") === "dark" ||
    document.documentElement.getAttribute("data-theme") === "dark" ||
    document.body.getAttribute("data-theme") === "dark"
  )
}

const buildStripeAppearance = () => {
  if (isDarkModeEnabled()) {
    return {
      theme: "night" as const,
      variables: {
        colorPrimary: "#f97316",
        colorBackground: "#18181b",
        colorText: "#f5f5f5",
        colorTextSecondary: "#a1a1aa",
        colorDanger: "#ef4444",
        borderRadius: "8px",
      },
    }
  }

  return {
    theme: "stripe" as const,
    variables: {
      colorPrimary: "#f97316",
      borderRadius: "8px",
    },
  }
}

const SavePaymentMethodModal: React.FC<Props> = ({
  payableType,
  payableId,
  stripePublishableKey,
  paymentOwner,
  mode = "add",
  existingSavedPaymentMethod = null,
  onClose,
  onSuccess,
}) => {
  const toast = useToast()
  const { venueSlug } = getAppConfig()

  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState("")

  const stripeRef = useRef<any>(null)
  const elementsRef = useRef<any>(null)
  const paymentElementRef = useRef<any>(null)
  const elementContainerRef = useRef<HTMLDivElement | null>(null)

  const ownerName = paymentOwner?.name?.trim() || ""
  const ownerEmail = paymentOwner?.email?.trim() || ""
  const ownerPhone = paymentOwner?.phone?.trim() || ""

  const isUpdateMode = mode === "update"

  useEffect(() => {
    let isMounted = true

    const initialize = async () => {
      try {
        setIsLoading(true)
        setError("")

        if (!stripePublishableKey) {
          throw new Error("Stripe publishable key is missing.")
        }

        const setupIntentResponse = await apiSavedPaymentMethods.setupIntent({
          data: {
            venueSlug,
            payableType,
            payableId,
          },
        })

        const clientSecret = setupIntentResponse?.clientSecret
        const stripeConnectedAccountId = setupIntentResponse?.stripeConnectedAccountId
        if (!clientSecret) {
          throw new Error("Stripe client secret is missing.")
        }

        const stripe = await loadStripe(
          stripePublishableKey,
          stripeConnectedAccountId ? { stripeAccount: stripeConnectedAccountId } : {}
        )
        if (!stripe) {
          throw new Error("Failed to initialize Stripe.")
        }

        if (!isMounted) return

        stripeRef.current = stripe
        elementsRef.current = stripe.elements({
          clientSecret,
          appearance: buildStripeAppearance(),
        })

        paymentElementRef.current = elementsRef.current.create("payment", {
          layout: "tabs",
          defaultValues: {
            billingDetails: {
              name: ownerName || undefined,
              email: ownerEmail || undefined,
              phone: ownerPhone || undefined,
            },
          },
          fields: {
            billingDetails: {
              name: ownerName ? "never" : "auto",
              email: ownerEmail ? "never" : "auto",
              phone: ownerPhone ? "never" : "auto",
            },
          },
        })

        if (elementContainerRef.current) {
          paymentElementRef.current.mount(elementContainerRef.current)
        }
      } catch (e: any) {
        setError(e?.body?.errors?.join(", ") || e.message || "Failed to initialize payment form.")
      } finally {
        if (isMounted) setIsLoading(false)
      }
    }

    initialize()

    return () => {
      isMounted = false
      paymentElementRef.current?.unmount?.()
      paymentElementRef.current = null
      elementsRef.current = null
      stripeRef.current = null
    }
  }, [payableId, payableType, stripePublishableKey, venueSlug, ownerName, ownerEmail, ownerPhone])

  const handleSave = async () => {
    if (isSubmitting) return

    try {
      setIsSubmitting(true)
      setError("")

      const stripe = stripeRef.current
      const elements = elementsRef.current

      if (!stripe || !elements) {
        throw new Error("Payment form is not ready.")
      }

      const result = await stripe.confirmSetup({
        elements,
        redirect: "if_required",
        confirmParams: {
          payment_method_data: {
            billing_details: {
              name: ownerName || undefined,
              email: ownerEmail || undefined,
              phone: ownerPhone || undefined,
            },
          },
        },
      })

      if (result.error) {
        throw new Error(result.error.message || "Failed to save card.")
      }

      if (!result.setupIntent || result.setupIntent.status !== "succeeded") {
        throw new Error("Failed to save card.")
      }

      await apiSavedPaymentMethods.create({
        data: {
          venueSlug,
          payableType,
          payableId,
          setupIntentId: result.setupIntent.id,
        },
      })

      toast(isUpdateMode ? "Card updated successfully" : "Card saved successfully", "success")
      await onSuccess()
      onClose()
    } catch (e: any) {
      const message = e?.body?.errors?.join(", ") || e.message || "Failed to save card."
      setError(message)
      toast(message, "danger")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <CatchModal
      isOpen={true}
      onClose={onClose}
      title={isUpdateMode ? "Update Saved Card" : "Add Saved Card"}
      size="lg"
    >
      <div className="space-y-4">
        <div className="alert alert-info mb-3">
          {isUpdateMode
            ? `Save a new card and make it the default card on file${
                existingSavedPaymentMethod?.displayName
                  ? ` instead of ${existingSavedPaymentMethod.displayName}`
                  : "."
              }`
            : "Save a card for future charges. No payment will be taken now."}
        </div>

        <div style={{ minHeight: "120px" }}>
          {isLoading && <div className="text-muted mb-3">Loading payment form...</div>}
          <div ref={elementContainerRef} />
          {error && <div className="text-danger mt-3">{error}</div>}
        </div>

        <div className="d-flex gap-2 mt-4">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary ms-auto"
            disabled={isSubmitting}
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleSave}
            className="btn btn-primary"
            disabled={isLoading || isSubmitting}
          >
            {isSubmitting
              ? isUpdateMode
                ? "Updating..."
                : "Saving..."
              : isUpdateMode
              ? "Update Card"
              : "Save Card"}
          </button>
        </div>
      </div>
    </CatchModal>
  )
}

export default SavePaymentMethodModal
